<template>
  <div class="flex flex-col gap-3 min-h-[200px]">
    <div class="flex items-center gap-2">
      <div class="text-sm text-text-subtle flex-1">
        {{ $t('scheduled.approvals.intro') }}
      </div>
      <button
        class="text-xs px-2 py-1 rounded bg-transparent outline outline-1 outline-brand-outline text-text-link flex-shrink-0"
        @click="toggleHistory"
      >{{ showing_history ? $t('scheduled.approvals.hideHistory') : $t('scheduled.approvals.history') }}</button>
    </div>

    <div v-if="processing" class="px-2 py-6 text-sm text-text-subtle">{{ $t('scheduled.common.loading') }}</div>

    <div v-else-if="!drafts.length" class="px-2 py-8 text-center text-sm text-text-subtle">
      {{ $t('scheduled.approvals.emptyQueue') }}
    </div>

    <template v-else>
      <!-- Batch bar -->
      <div class="flex items-center gap-2">
        <button
          class="text-xs px-2 py-1 rounded bg-transparent outline outline-1 outline-brand-outline text-text-link"
          :disabled="processing || !selected_ids.length"
          @click="batchApprove"
        >{{ $t('scheduled.approvals.approveSelected', { count: selected_ids.length }) }}</button>
        <span class="text-xs text-text-subtle">{{ $t('scheduled.approvals.pendingCount', drafts.length) }}</span>
      </div>

      <div class="flex flex-col gap-2">
        <div
          v-for="draft in drafts"
          :key="draft.id"
          class="flex items-start gap-3 p-3 bg-surface-1 border border-border-separator rounded-md"
        >
          <input type="checkbox" class="mt-1" :value="draft.id" v-model="selected_ids" />
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2">
              <span class="text-sm font-medium text-text-default truncate">{{ draft.title || draft.actionType }}</span>
              <span class="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-brand-subtle text-text-link flex-shrink-0">
                {{ draft.actionType }}
              </span>
            </div>

            <textarea
              v-if="editing_id === draft.id"
              v-model="edit_content"
              rows="3"
              class="mt-1 w-full text-sm px-2 py-1.5 bg-surface-2 border border-border-separator rounded-md text-text-default outline-none transition-[background-color,border-color,box-shadow] focus:bg-field-focus focus:border-[#0855F9] focus:shadow-[0_0_0_3px_var(--shadow-focus-input)]"
            ></textarea>
            <div v-else class="text-xs text-text-subtle whitespace-pre-wrap">{{ draft.content }}</div>

            <div class="flex items-center gap-2 mt-2">
              <template v-if="editing_id === draft.id">
                <button class="text-xs px-2 py-1 rounded outline outline-1 outline-brand-outline text-text-link" :disabled="processing" @click="saveEdit(draft)">{{ $t('scheduled.approvals.save') }}</button>
                <button class="text-xs px-2 py-1 rounded text-text-subtle" @click="cancelEdit">{{ $t('common.cancel') }}</button>
              </template>
              <template v-else>
                <button class="text-xs px-2 py-1 rounded outline outline-1 outline-brand-outline text-text-link" :disabled="processing" @click="approveDraft(draft)">{{ $t('scheduled.approvals.approve') }}</button>
                <button class="text-xs px-2 py-1 rounded text-text-subtle" :disabled="processing" @click="dismissDraft(draft)">{{ $t('common.dismiss') }}</button>
                <button class="text-xs px-2 py-1 rounded text-text-subtle" @click="startEdit(draft)">{{ $t('scheduled.approvals.edit') }}</button>
              </template>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- What already happened. Until this existed, a settled outcome lived for the four seconds of a
         flash message and then became unreachable: the queue lists Pending only, so an action that failed
         with "Nothing can carry out a 'parts_purchase' action yet." was indistinguishable from one that
         went out. executionDetail is the field four separate places call "the detail the owner reads". -->
    <template v-if="showing_history">
      <div class="pt-2 text-[11px] uppercase tracking-wide text-text-subtle">{{ $t('scheduled.approvals.historyTitle') }}</div>

      <div v-if="history_processing" class="px-2 py-3 text-xs text-text-subtle">{{ $t('scheduled.common.loading') }}</div>

      <div v-else-if="!history.length" class="px-2 py-3 text-xs text-text-subtle">
        {{ $t('scheduled.approvals.historyEmpty') }}
      </div>

      <div v-else class="flex flex-col gap-1">
        <div
          v-for="item in history"
          :key="item.id"
          class="flex items-start gap-2 px-3 py-2 bg-surface-1 border border-border-separator rounded-md"
        >
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2 flex-wrap">
              <span class="text-sm text-text-default truncate">{{ item.title || item.actionType }}</span>
              <span class="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-surface-2 text-text-subtle flex-shrink-0">
                {{ item.actionType }}
              </span>
              <span :class="historyStatusClass(item.status)" class="text-xs flex-shrink-0">
                {{ historyStatusLabel(item.status) }}
              </span>
            </div>
            <div v-if="item.executionDetail" class="text-xs text-text-subtle mt-0.5">
              {{ item.executionDetail }}
            </div>
          </div>
        </div>
      </div>
    </template>

    <div v-if="row_message" class="text-xs text-text-subtle px-1">{{ row_message }}</div>
  </div>
</template>

<script>
import { mapStores } from 'pinia';
import { useMainStore } from '@store/main';

export default {
  name: 'ActionApprovalQueue',
  emits: ['count'],
  data() {
    return {
      drafts: [],
      selected_ids: [],
      editing_id: '',
      edit_content: '',
      processing: false,
      row_message: '',
      history: [],
      history_processing: false,
      showing_history: false,
    };
  },
  mounted() {
    this.loadDrafts();
  },
  computed: {
    ...mapStores(useMainStore),
  },
  methods: {
    loadDrafts() {
      const self = this;
      this.processing = true;
      this.mainStore.GetActionDrafts().then((response) => {
        self.drafts = Array.isArray(response) ? response : [];
        self.selected_ids = [];
        self.processing = false;
        self.$emit('count', self.drafts.length);
      }, () => {
        self.drafts = [];
        self.processing = false;
        self.flashMessage(self.$t('scheduled.approvals.queueLoadFailed'));
      });
    },
    // Loaded on demand rather than with the queue: most visits are here to approve something, and a second
    // request on every open would be paid by everyone to serve the few who look back.
    toggleHistory() {
      this.showing_history = !this.showing_history;
      if (this.showing_history) this.loadHistory();
    },
    loadHistory() {
      const self = this;
      this.history_processing = true;
      this.mainStore.GetSettledActionDrafts().then((response) => {
        self.history = Array.isArray(response) ? response : [];
        self.history_processing = false;
      }, () => {
        self.history = [];
        self.history_processing = false;
        self.flashMessage(self.$t('scheduled.approvals.historyLoadFailed'));
      });
    },
    // "Failed" reads as the system's fault; an action nobody could carry out is not that. The distinction
    // matters because the commonest cause today is that no executor exists for the type yet.
    historyStatusLabel(status) {
      const keys = {
        Executed: 'scheduled.approvals.statusExecuted',
        ExecutionFailed: 'scheduled.approvals.statusExecutionFailed',
        Dismissed: 'scheduled.approvals.statusDismissed',
        Expired: 'scheduled.approvals.statusExpired',
      };
      return keys[status] ? this.$t(keys[status]) : status;
    },
    historyStatusClass(status) {
      if (status === 'ExecutionFailed') return 'text-danger';
      if (status === 'Executed') return 'text-text-link';
      return 'text-text-subtle';
    },
    approveDraft(draft) {
      this.decide(draft, 'approve', this.approveOutcomeMessage);
    },
    dismissDraft(draft) {
      this.decide(draft, 'dismiss', () => this.$t('scheduled.approvals.dismissedMessage'));
    },
    // `describe` builds the flash from the RESPONSE rather than being a fixed string: approving carries
    // out the action, and the reloaded list is Pending-only, so a hardcoded 'Approved.' was the only thing
    // the owner ever saw — an action that failed with "Nothing can carry out a 'parts_purchase' action
    // yet." looked exactly like one that went out.
    decide(draft, decision, describe) {
      if (this.processing) return;
      const self = this;
      this.processing = true;
      this.mainStore.SetActionDraftDecision({ id: draft.id, decision }).then((response) => {
        self.processing = false;
        self.flashMessage(describe(response));
        self.loadDrafts();
        // The decision just added a row to it, so a history left on screen would be a list that
        // contradicts the flash message directly above it.
        if (self.showing_history) self.loadHistory();
      }, () => {
        self.processing = false;
        self.flashMessage(self.$t('scheduled.approvals.updateFailed'));
      });
    },
    // The approve response IS the settled draft — the controller re-reads it after execution — so its
    // status and executionDetail are the real outcome. executionDetail is the field four places call "the
    // detail the owner reads", and this is where it is finally read.
    approveOutcomeMessage(settled) {
      const status = settled && settled.status;
      const detail = (settled && settled.executionDetail) || '';
      if (status === 'Executed') return detail || this.$t('scheduled.approvals.approvedCarriedOut');
      if (status === 'ExecutionFailed') {
        return detail
          ? this.$t('scheduled.approvals.approvedNotCarriedOutDetail', { detail })
          : this.$t('scheduled.approvals.approvedNotCarriedOut');
      }
      // Still Approved (or a shape this build does not know): nothing executed, which is exactly how this
      // queue behaved before an executor existed. Saying more than 'Approved.' here would be a guess.
      return this.$t('scheduled.approvals.approved');
    },
    startEdit(draft) {
      this.editing_id = draft.id;
      this.edit_content = draft.content || '';
    },
    cancelEdit() {
      this.editing_id = '';
      this.edit_content = '';
    },
    saveEdit(draft) {
      if (this.processing) return;
      const self = this;
      this.processing = true;
      this.mainStore.EditActionDraft({ id: draft.id, content: this.edit_content }).then(() => {
        self.processing = false;
        self.cancelEdit();
        self.flashMessage(self.$t('scheduled.approvals.draftUpdated'));
        self.loadDrafts();
      }, () => {
        self.processing = false;
        self.flashMessage(self.$t('scheduled.approvals.editSaveFailed'));
      });
    },
    batchApprove() {
      if (this.processing || !this.selected_ids.length) return;
      const self = this;
      const ids = this.selected_ids.slice();
      this.processing = true;
      this.mainStore.BatchApproveActionDrafts(ids).then((response) => {
        self.processing = false;
        self.flashMessage(self.batchOutcomeMessage(response, ids.length));
        self.loadDrafts();
      }, () => {
        self.processing = false;
        self.flashMessage(self.$t('scheduled.approvals.batchApproveFailed'));
      });
    },
    // `count` is how many ids the server ACCEPTED, not how many we sent and not how many ran: the batch
    // skips drafts that were already dismissed or expired, and skips any whose store has been withdrawn
    // from the beta. executed/failed are what spec §7 asks this response for — "one failure does not stop
    // the rest, and the response names the one that broke". Naming it is the point, so `rooftopClosed`
    // gets named for the same reason: a row that silently did not move reads as a row that worked.
    batchOutcomeMessage(response, requested) {
      if (!response || typeof response.executed !== 'number') {
        // A build without the execution boundary wired: approval landed, nothing ran.
        return this.$t('scheduled.approvals.batchApprovedDrafts', { count: requested });
      }
      const failed = Array.isArray(response.failed) ? response.failed : [];
      const closed = Array.isArray(response.rooftopClosed) ? response.rooftopClosed : [];
      const approved = typeof response.count === 'number' ? response.count : requested - closed.length;
      let msg = this.$t('scheduled.approvals.batchOutcome', { approved, executed: response.executed });
      if (failed.length) {
        const reasons = failed.map(f => (f && (f.detail || f.id)) || this.$t('scheduled.approvals.noReasonGiven')).join(' ');
        msg += ' ' + this.$t('scheduled.approvals.batchFailed', { count: failed.length, reasons });
      }
      if (closed.length) {
        msg += ' ' + this.$t('scheduled.approvals.batchRooftopClosed', closed.length);
      }
      return msg;
    },
    flashMessage(msg) {
      const self = this;
      this.row_message = msg;
      setTimeout(() => { if (self.row_message === msg) self.row_message = ''; }, 4000);
    },
  },
};
</script>
